import { LuIcon, Text } from "@overmap-ai/blocks"
import { EMPTY_ARRAY, type User } from "@overmap-ai/common"
import { use, useCallback, useMemo } from "react"

import { UserMultiSelect } from "../../../components/DropdownMultiSelects/UserMultiSelect"
import { UserSelect } from "../../../components/DropdownSelects/UserSelect"
import { UserIcon } from "../../../components/Icons/UserIcon"
import { modelIconMapping } from "../../../mappings/icons"
import { getUserDisplayLabel } from "../../../utils/users"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { CreatedByFilterDef } from "./CreatedByFilterDef"

type CreatedByFilterCellProps = FilterDefRenderProps<CreatedByFilterDef>

export const CreatedByFilterCell = (props: CreatedByFilterCellProps) => {
	const { filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const users = filterDef.users
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedUser = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.equals.isFilterValueValid(filterValue)) return undefined
		return users.find(({ id }) => id === filterValue)
	}, [users, filterValue, filterDef.modifiers.equals])

	const selectedUsers = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.includes.isFilterValueValid(filterValue)) return undefined
		const set = new Set(filterValue)
		return users.filter(({ id }) => set.has(id))
	}, [users, filterValue, filterDef.modifiers.includes])

	const handleValueSelect = useCallback(
		(user?: User) => {
			filter.setFilterValue(user?.id)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(users: User[]) => {
			if (users.length > 0) {
				filter.setFilterValue(users.map((user) => user.id))
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<UserSelect
						required={false}
						value={selectedUser}
						onValueChange={handleValueSelect}
						users={users}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow">
							{equalsFilterValue !== undefined ? (
								<>
									<UserIcon userId={equalsFilterValue} size="xs" />
									<Text className="truncate">{getUserDisplayLabel(selectedUser)}</Text>
								</>
							) : (
								"..."
							)}
						</FilterCellButton>
					</UserSelect>
				)
			}
			case "includes":
			case "excludes": {
				const getLabelUi = () => {
					switch ((selectedUsers ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1:
							return (
								<>
									<UserIcon userId={selectedUsers![0].id} size="xs" />
									<Text className="truncate">{getUserDisplayLabel(selectedUsers![0])}</Text>
								</>
							)
						default:
							return (
								<span className="flex items-center gap-1 truncate">
									{selectedUsers!.slice(0, 3).map((user) => (
										<UserIcon key={user.id} userId={user.id} size="xs" />
									))}
									<Text className="truncate">{`${selectedUsers!.length} users`}</Text>
								</span>
							)
					}
				}

				return (
					<UserMultiSelect
						values={selectedUsers}
						onValuesChange={handleValuesChange}
						users={users}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow truncate">{getLabelUi()}</FilterCellButton>
					</UserMultiSelect>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.creator} />
				Creator
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
